import { useEffect, useMemo, useRef, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { hasPlan } from "@/lib/planAccess";
import UpgradeGate from "@/components/UpgradeGate";
import {
  FileText,
  CheckCircle,
  Warning,
  ArrowRight,
  FileArrowUp,
  ShieldCheck,
  Target,
  Lightning,
  Wrench,
  MagnifyingGlass,
  Copy,
} from "@phosphor-icons/react";

const MAX_FILE_SIZE = 4 * 1024 * 1024;

export default function ResumeReview() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");

  const [mode, setMode] = useState("file");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const refreshUsage = () =>
      api.get("/billing/usage")
        .then(({ data }) => { if (!cancelled) setUsage(data); })
        .catch(() => {});

    const poll = (jobId) => {
      if (!jobId || cancelled) return;
      setLoading(true);
      const check = async () => {
        try {
          const { data } = await api.get(`/resume/review/status/${jobId}`);
          if (cancelled) return;
          if (data?.status === "completed" && data?.result) {
            setResult(data.result);
            setLoading(false);
            refreshUsage();
            toast.success("Resume review completed. You can continue using Career GPS while it analyzes.");
            if (timer) clearInterval(timer);
            return;
          }
          if (data?.status === "failed") {
            setLoading(false);
            refreshUsage();
            toast.error(data.error || "Resume review failed. Please try again.");
            if (timer) clearInterval(timer);
          }
        } catch (err) {
          if (!cancelled) console.error("resume review status failed", err);
        }
      };
      check();
      timer = setInterval(check, 2500);
    };

    refreshUsage();
    api.get("/resume/review/latest")
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.result) setResult(data.result);
        if (data?.status === "processing" && data?.job_id) {
          poll(data.job_id);
        } else if (data?.status === "failed") {
          toast.error(data.error || "Resume review failed. Please try again.");
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  const wordCount = useMemo(() => {
    return text.trim()
      ? text.trim().split(/\s+/).length
      : 0;
  }, [text]);

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];

    if (!f) return;

    if (f.size > MAX_FILE_SIZE) {
      toast.error(
        "File too large. Maximum size is 4 MB."
      );
      e.target.value = "";
      return;
    }

    const okExt = /\.(pdf|docx|txt)$/i.test(
      f.name
    );

    if (!okExt) {
      toast.error(
        "Use a PDF, DOCX, or TXT file."
      );
      e.target.value = "";
      return;
    }

    setFile(f);
    setResult(null);
  };

  const submit = async () => {
    if (mode === "paste") {
      if (text.trim().length < 80) {
        toast.error(
          "Paste your full resume. At least 80 characters are required."
        );
        return;
      }
    } else if (!file) {
      toast.error(
        "Choose your resume PDF or DOCX first."
      );
      return;
    }

    setLoading(true);
    setResult(null);
    let backgroundJobStarted = false;

    try {
      const form = new FormData();

      if (mode === "file") {
        form.append("file", file);
      } else {
        form.append(
          "resume_text",
          text.trim()
        );
      }

      const { data } = await api.post(
        "/resume/review",
        form,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      if (data?.status === "completed" && data?.result) {
        setResult(data.result);
        toast.success(data.cached ? "Saved resume review restored." : "Full resume review completed. +25 XP");
        api.get("/billing/usage").then(({ data: usageData }) => setUsage(usageData)).catch(() => {});
        return;
      }

      if (data?.status === "processing" && data?.job_id) {
        backgroundJobStarted = true;
        toast.success("Resume uploaded. Analysis is continuing in the background — you can use other features now.");

        const jobId = data.job_id;
        const poll = async () => {
          try {
            const { data: status } = await api.get(`/resume/review/status/${jobId}`);
            if (status?.status === "completed" && status?.result) {
              setResult(status.result);
              setLoading(false);
              toast.success("Resume review completed.");
              api.get("/billing/usage").then(({ data: usageData }) => setUsage(usageData)).catch(() => {});
              return true;
            }
            if (status?.status === "failed") {
              setLoading(false);
              toast.error(status.error || "Resume review failed. Please try again.");
              api.get("/billing/usage").then(({ data: usageData }) => setUsage(usageData)).catch(() => {});
              return true;
            }
          } catch (pollErr) {
            console.error("resume review polling failed", pollErr);
          }
          return false;
        };

        let stopped = false;
        const pollLoop = async () => {
          while (!stopped) {
            const done = await poll();
            if (done) break;
            await new Promise((resolve) => setTimeout(resolve, 2500));
          }
        };
        // The server owns the job. This client poll is only for updating this page;
        // navigating away does not cancel the server-side analysis.
        pollLoop();
        return () => { stopped = true; };
      }
    } catch (err) {
      console.error(
        "resume review failed",
        err
      );

      const detail =
        err.response?.data?.detail;

      toast.error(
        detail ||
          "We couldn't review this resume. Please try again."
      );
    } finally {
      if (!backgroundJobStarted) setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#050505]"
      data-testid="resume-page"
    >
      <Navbar />
      <DashSidebar />

      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-6xl mx-auto lg:mx-0">
        <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">
          // AI Review
        </div>

        <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium mb-2">
          Resume Review
        </h1>

        <p className="text-zinc-500 mb-2 text-sm sm:text-base max-w-3xl">
          Get a recruiter-level review of your
          resume, including ATS compatibility,
          content mistakes, keyword gaps,
          formatting risks, weak bullets, and
          exact improvements you can make.
        </p>

        <p className="text-xs text-zinc-600 mb-7">
          {usage
            ? `${usage.used?.resume_reviews || 0}/${usage.limits?.resume_reviews || 0} reviews used this month`
            : ""}
        </p>

        <div className="grid xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-6 items-start">
          <div className="bg-[#0B0B0D] border border-white/10 rounded-2xl p-4 sm:p-5">
            <div
              className="flex gap-2 mb-4"
              data-testid="resume-mode-tabs"
            >
              <button
                onClick={() =>
                  setMode("file")
                }
                data-testid="mode-file"
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                  mode === "file"
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-white/5 border-white/10 text-zinc-300"
                }`}
              >
                Upload file
              </button>

              <button
                onClick={() =>
                  setMode("paste")
                }
                data-testid="mode-paste"
                className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                  mode === "paste"
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-white/5 border-white/10 text-zinc-300"
                }`}
              >
                Paste text
              </button>
            </div>

            {mode === "file" ? (
              <>
                <label className="text-xs tracking-widest uppercase text-zinc-400 font-semibold">
                  Resume file
                </label>

                <div
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                  data-testid="resume-dropzone"
                  className="mt-2 cursor-pointer bg-[#0F0F12] border-2 border-dashed border-white/10 hover:border-blue-500/50 rounded-xl px-6 py-12 text-center transition-colors"
                >
                  <FileArrowUp
                    size={40}
                    weight="duotone"
                    className="mx-auto text-blue-400 mb-3"
                  />

                  {file ? (
                    <div>
                      <div className="text-sm font-medium text-white break-all">
                        {file.name}
                      </div>

                      <div className="text-xs text-zinc-500 mt-1">
                        {(file.size / 1024).toFixed(
                          1
                        )}{" "}
                        KB · Click to change
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm text-zinc-300">
                        Choose your PDF or DOCX
                        resume
                      </div>

                      <div className="text-xs text-zinc-500 mt-1">
                        PDF, DOCX or TXT ·
                        maximum 4 MB
                      </div>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={onFileSelected}
                  className="hidden"
                  data-testid="resume-file-input"
                />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <label className="text-xs tracking-widest uppercase text-zinc-400 font-semibold">
                    Paste your resume
                  </label>

                  <span className="text-xs text-zinc-600">
                    {wordCount} words
                  </span>
                </div>

                <textarea
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    setResult(null);
                  }}
                  rows={19}
                  data-testid="resume-textarea"
                  placeholder="Paste your complete resume here..."
                  className="mt-2 w-full bg-[#0F0F12] border border-white/10 focus:border-blue-500 rounded-xl px-4 py-3 text-sm outline-none resize-y text-zinc-200 placeholder:text-zinc-600"
                />
              </>
            )}

            <div className="mt-4 rounded-xl border border-white/5 bg-white/[0.02] p-3 text-xs text-zinc-500 flex gap-2">
              <ShieldCheck
                size={16}
                className="text-blue-400 shrink-0"
              />

              Your resume is analyzed for this
              review and the result is saved to
              your Career GPS account.
            </div>

            <button
              onClick={submit}
              disabled={loading || Number(usage?.used?.resume_reviews || 0) >= Number(usage?.limits?.resume_reviews || 0)}
              data-testid="resume-submit"
              className="mt-4 w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-lg inline-flex items-center justify-center gap-2"
            >
              {loading
                ? "Analyzing every section..."
                : "Get Full AI Review"}

              {!loading && (
                <ArrowRight size={16} />
              )}
            </button>
          </div>

          <div data-testid="resume-result">
            {!loading && usage && Number(usage.used?.resume_reviews || 0) >= Number(usage.limits?.resume_reviews || 0) && (
              <UpgradeGate
                title="Your monthly resume review limit is reached"
                description={`You have used all ${usage.limits?.resume_reviews || 0} Resume Reviews included in your ${usage.tier === "premium" ? "Premium" : usage.tier === "pro" ? "Pro" : "Free"} plan this month.`}
              />
            )}
            {!result && !loading && (
              <EmptyState />
            )}

            {loading && <LoadingState />}

            {result && (
              <ReviewResult
                result={result}
                limited={!paid}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8 min-h-[420px] flex items-center justify-center text-center">
      <div className="max-w-sm">
        <FileText
          size={42}
          weight="duotone"
          className="mx-auto text-blue-500 mb-4"
        />

        <h3 className="text-lg font-medium text-white">
          Your full review will appear here
        </h3>

        <p className="text-sm text-zinc-500 mt-2">
          Upload your resume and we’ll check
          content, ATS compatibility, keywords,
          formatting risks, clarity, achievements,
          and practical rewrites.
        </p>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="bg-[#0F0F12] border border-white/10 rounded-2xl p-8 min-h-[420px] flex items-center justify-center text-center">
      <div>
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-blue-500 animate-spin mx-auto" />

        <h3 className="text-lg font-medium mt-5">
          Analyzing your resume...
        </h3>

        <p className="text-sm text-zinc-500 mt-2">
          Checking ATS compatibility, content
          quality, keywords and recruiter impact.
        </p>
      </div>
    </div>
  );
}

function ReviewResult({
  result,
  limited = false,
}) {
  const checks =
    result.deterministic_checks || {};

  const breakdown =
    result.score_breakdown || {};

  if (limited) {
    return (
      <div className="space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <ScoreCard
            label="Overall"
            value={result.overall_score}
            icon={Target}
          />

          <ScoreCard
            label="ATS"
            value={
              result.ats_score ??
              result.ats_compatibility
            }
            icon={ShieldCheck}
          />

          <ScoreCard
            label="Recruiter"
            value={result.recruiter_score}
            icon={MagnifyingGlass}
          />
        </div>

        <UpgradeGate
          title="Your full resume report is locked"
          description="Free includes 1 resume review per month with limited results. Upgrade to Pro or Premium to see the full recruiter and ATS report."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-3">
        <ScoreCard
          label="Overall"
          value={result.overall_score}
          icon={Target}
        />

        <ScoreCard
          label="ATS"
          value={
            result.ats_score ??
            result.ats_compatibility
          }
          icon={ShieldCheck}
        />

        <ScoreCard
          label="Recruiter"
          value={result.recruiter_score}
          icon={MagnifyingGlass}
        />
      </div>

      <Section
        title="Executive assessment"
        icon={FileText}
      >
        <p className="text-sm leading-6 text-zinc-300">
          {result.summary ||
            "No summary was returned."}
        </p>

        {result.target_role && (
          <div className="mt-3 text-xs text-zinc-500">
            Target role:{" "}
            <span className="text-zinc-300">
              {result.target_role}
            </span>
          </div>
        )}
      </Section>

      <Section
        title="Score breakdown"
        icon={Target}
      >
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            ["Content", breakdown.content],
            ["ATS", breakdown.ats],
            ["Impact", breakdown.impact],
            ["Clarity", breakdown.clarity],
            [
              "Formatting",
              breakdown.formatting,
            ],
            ["Relevance", breakdown.relevance],
          ].map(([label, value]) => (
            <MiniScore
              key={label}
              label={label}
              value={value}
            />
          ))}
        </div>
      </Section>

      {!!(result.top_problems || []).length && (
        <Section
          title="Most important problems"
          icon={Warning}
          accent="amber"
        >
          <div className="space-y-3">
            {result.top_problems.map(
              (item, i) => (
                <div
                  key={`${item.problem}-${i}`}
                  className="rounded-lg border border-white/5 bg-white/[0.02] p-3"
                >
                  <div className="flex items-start gap-2">
                    <Severity
                      severity={item.severity}
                    />

                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white">
                        {item.problem}
                      </div>

                      {item.why_it_matters && (
                        <p className="text-xs text-zinc-500 mt-1">
                          Why:{" "}
                          {item.why_it_matters}
                        </p>
                      )}

                      {item.fix && (
                        <p className="text-sm text-zinc-300 mt-2">
                          Fix: {item.fix}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </Section>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <ListSection
          title="Strengths"
          items={result.strengths}
          icon={CheckCircle}
          accent="emerald"
        />

        <ListSection
          title="Weaknesses"
          items={result.weaknesses}
          icon={Warning}
          accent="amber"
        />
      </div>

      {!!(result.missing_keywords || [])
        .length && (
        <Section
          title="Missing high-value keywords"
          icon={MagnifyingGlass}
          accent="amber"
        >
          <div className="flex flex-wrap gap-2">
            {result.missing_keywords.map(
              (k) => (
                <span
                  key={k}
                  className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20"
                >
                  {k}
                </span>
              )
            )}
          </div>
        </Section>
      )}

      {!!(result.keyword_matches || [])
        .length && (
        <Section
          title="Keyword alignment"
          icon={MagnifyingGlass}
        >
          <div className="space-y-2">
            {result.keyword_matches.map(
              (item, i) => (
                <div
                  key={`${item.keyword}-${i}`}
                  className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 last:border-0 last:pb-0"
                >
                  <span className="text-sm text-zinc-300">
                    {item.keyword}
                  </span>

                  <span
                    className={`text-[10px] uppercase tracking-widest ${
                      item.evidence ===
                      "present"
                        ? "text-emerald-400"
                        : item.evidence ===
                          "weak"
                        ? "text-amber-400"
                        : "text-red-400"
                    }`}
                  >
                    {item.evidence ||
                      "unknown"}
                  </span>
                </div>
              )
            )}
          </div>
        </Section>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <ListSection
          title="Content issues"
          items={result.content_issues}
          icon={Wrench}
          accent="amber"
        />

        <ListSection
          title="Grammar & clarity"
          items={result.grammar_and_clarity}
          icon={FileText}
        />
      </div>

      <ListSection
        title="Formatting & ATS risks"
        items={result.formatting_issues}
        icon={ShieldCheck}
        accent="amber"
      />

      {!!(result.experience_feedback || [])
        .length && (
        <Section
          title="Experience & bullet review"
          icon={Lightning}
        >
          <div className="space-y-3">
            {result.experience_feedback.map(
              (item, i) => (
                <div
                  key={`${item.role}-${i}`}
                  className="rounded-lg border border-white/5 p-3"
                >
                  <div className="text-sm font-medium text-white">
                    {item.role ||
                      "Experience"}
                  </div>

                  {item.issue && (
                    <p className="text-sm text-zinc-500 mt-1">
                      Issue: {item.issue}
                    </p>
                  )}

                  {item.fix && (
                    <p className="text-sm text-zinc-300 mt-2">
                      Improvement:{" "}
                      {item.fix}
                    </p>
                  )}
                </div>
              )
            )}
          </div>
        </Section>
      )}

      {!!(result.rewrite_examples || [])
        .length && (
        <Section
          title="Before → after rewrite examples"
          icon={ArrowRight}
          accent="blue"
        >
          <div className="space-y-4">
            {result.rewrite_examples.map(
              (item, i) => (
                <div
                  key={`${item.section}-${i}`}
                  className="rounded-xl border border-white/5 bg-black/20 p-4"
                >
                  <div className="text-xs uppercase tracking-widest text-blue-400">
                    {item.section ||
                      "Resume"}
                  </div>

                  {item.original && (
                    <p className="text-sm text-zinc-500 mt-3">
                      <span className="text-zinc-600">
                        Before:
                      </span>{" "}
                      {item.original}
                    </p>
                  )}

                  {item.improved && (
                    <p className="text-sm text-zinc-200 mt-2">
                      <span className="text-blue-400">
                        After:
                      </span>{" "}
                      {item.improved}
                    </p>
                  )}

                  {item.reason && (
                    <p className="text-xs text-zinc-600 mt-2">
                      {item.reason}
                    </p>
                  )}

                  {item.improved && (
                    <button
                      type="button"
                      onClick={() =>
                        navigator.clipboard?.writeText(
                          item.improved
                        )
                      }
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white"
                    >
                      <Copy size={13} />
                      Copy improved version
                    </button>
                  )}
                </div>
              )
            )}
          </div>
        </Section>
      )}

      <Section
        title="ATS technical checks"
        icon={ShieldCheck}
      >
        <div className="grid sm:grid-cols-2 gap-2">
          <CheckRow
            label="Standard headings"
            value={
              result.ats_checks
                ?.standard_headings
            }
          />

          <CheckRow
            label="Readable contact details"
            value={
              result.ats_checks
                ?.contact_readable
            }
          />

          <CheckRow
            label="Keyword alignment"
            value={
              result.ats_checks
                ?.keyword_alignment
            }
            numeric
          />

          <CheckRow
            label="Achievement bullets"
            value={
              result.ats_checks
                ?.achievement_bullets
            }
            numeric
          />

          <CheckRow
            label="Parseability"
            value={
              result.ats_checks
                ?.parseability
            }
            numeric
          />

          <CheckRow
            label="Date consistency"
            value={
              result.ats_checks
                ?.date_consistency
            }
            numeric
          />

          <CheckRow
            label="Length fit"
            value={
              result.ats_checks?.length_fit
            }
            numeric
          />
        </div>

        {checks.word_count ? (
          <p className="text-xs text-zinc-600 mt-3">
            Extracted {checks.word_count} words
            from{" "}
            {checks.line_count || "the"} lines.
          </p>
        ) : null}
      </Section>

      {!!(result.action_plan || []).length && (
        <Section
          title="Your priority action plan"
          icon={ArrowRight}
          accent="blue"
        >
          <div className="space-y-3">
            {result.action_plan.map(
              (item, i) => (
                <div
                  key={`${item.priority}-${i}`}
                  className="flex gap-3"
                >
                  <div className="w-7 h-7 rounded-full bg-blue-500/10 text-blue-400 flex items-center justify-center text-xs font-medium shrink-0">
                    {item.priority ||
                      i + 1}
                  </div>

                  <div>
                    <div className="text-sm text-zinc-200">
                      {item.action}
                    </div>

                    {item.expected_impact && (
                      <div className="text-xs text-zinc-600 mt-1">
                        Expected impact:{" "}
                        {
                          item.expected_impact
                        }
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </Section>
      )}
    </div>
  );
}

function ScoreCard({
  label,
  value,
  icon: Icon,
}) {
  const score = Number.isFinite(
    Number(value)
  )
    ? Number(value)
    : null;

  const tone =
    score === null
      ? "text-zinc-500"
      : score >= 80
      ? "text-emerald-400"
      : score >= 60
      ? "text-blue-400"
      : "text-amber-400";

  return (
    <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-zinc-500">
        <Icon size={14} />
        {label}
      </div>

      <div
        className={`text-4xl font-medium font-mono mt-1 ${tone}`}
      >
        {score ?? "—"}
        <span className="text-zinc-600 text-lg">
          /100
        </span>
      </div>
    </div>
  );
}

function MiniScore({
  label,
  value,
}) {
  const score = Number.isFinite(
    Number(value)
  )
    ? Number(value)
    : null;

  return (
    <div className="flex items-center justify-between rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2">
      <span className="text-xs text-zinc-400">
        {label}
      </span>

      <span className="text-sm font-mono text-zinc-200">
        {score ?? "—"}/100
      </span>
    </div>
  );
}

function ListSection({
  title,
  items = [],
  icon,
  accent = "blue",
}) {
  return (
    <Section
      title={title}
      icon={icon}
      accent={accent}
    >
      {items.length ? (
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li
              key={`${item}-${i}`}
              className="text-sm leading-6 text-zinc-300 flex gap-2"
            >
              <span className="text-zinc-600 shrink-0">
                •
              </span>

              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-zinc-600">
          No major issues identified in this
          area.
        </p>
      )}
    </Section>
  );
}

function CheckRow({
  label,
  value,
  numeric = false,
}) {
  const shown = numeric
    ? value === undefined ||
      value === null
      ? "—"
      : `${value}/100`
    : value
    ? "Pass"
    : "Review";

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.02] border border-white/5 px-3 py-2">
      <span className="text-xs text-zinc-400">
        {label}
      </span>

      <span
        className={`text-xs ${
          numeric
            ? "text-zinc-200"
            : value
            ? "text-emerald-400"
            : "text-amber-400"
        }`}
      >
        {shown}
      </span>
    </div>
  );
}

function Severity({ severity }) {
  const normalized = String(
    severity || "medium"
  ).toLowerCase();

  const classes =
    normalized === "critical"
      ? "text-red-400 bg-red-500/10"
      : normalized === "high"
      ? "text-amber-400 bg-amber-500/10"
      : normalized === "low"
      ? "text-zinc-400 bg-white/5"
      : "text-blue-400 bg-blue-500/10";

  return (
    <span
      className={`text-[9px] uppercase tracking-widest px-2 py-1 rounded shrink-0 ${classes}`}
    >
      {normalized}
    </span>
  );
}

function Section({
  title,
  icon: Icon,
  children,
  accent = "blue",
}) {
  const c = {
    blue: "text-blue-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
  };

  return (
    <div className="bg-[#0F0F12] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon
          size={15}
          className={c[accent] || c.blue}
        />

        <h4 className="text-sm font-medium text-white">
          {title}
        </h4>
      </div>

      {children}
    </div>
  );
}
